import {
  ComponentPropsWithoutRef,
  forwardRef,
  useEffect,
  useState,
} from "react";
import styles from "./Drawer.module.scss";
import cx from "classnames";
import { useDrawerContext } from "./internal/useDrawerContext";

/**
 * Props for the DrawerContent component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type DrawerContentProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * When true, the content will stick below the header during scroll.
   * @default false
   */
  sticky?: boolean;
};

/**
 * Content container for the drawer that holds the main content area.
 *
 * Features:
 * - Flexible content area with proper spacing
 * - Scrollable content when needed
 * - Optional sticky positioning below header
 * - Proper semantic HTML structure
 * - Accessible with proper ARIA attributes
 * - Consistent styling with the design system
 * - Supports all standard div element props
 * - Responsive design that adapts to content
 * - Proper focus management
 *
 * @example
 * // Basic content
 * <Drawer.Content>
 *   <p>This is the main content of the drawer.</p>
 * </Drawer.Content>
 *
 * @example
 * // Sticky content (e.g., search field that remains visible while scrolling)
 * <Drawer.Content sticky>
 *   <TextField placeholder="Filter results..." />
 * </Drawer.Content>
 */
export const DrawerContent = forwardRef<HTMLDivElement, DrawerContentProps>(
  (props, ref) => {
    const { children, className, sticky = false, ...rest } = props;
    const { headerRef } = useDrawerContext();
    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {
      const headerElement = headerRef?.current;
      if (!sticky || !headerElement) return;

      const updateHeight = () => {
        setHeaderHeight(headerElement.offsetHeight || 0);
      };

      updateHeight();
      const resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(headerElement);

      return () => resizeObserver.disconnect();
    }, [sticky, headerRef]);

    const contentClassNames = cx(styles["content"], className, {
      [styles["sticky"]]: sticky,
    });

    const stickyStyle = sticky ? { top: `${headerHeight}px` } : undefined;

    return (
      <div
        className={contentClassNames}
        data-anv="drawer-content"
        ref={ref}
        style={{ ...stickyStyle, ...rest.style }}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

DrawerContent.displayName = "DrawerContent";
